import { useEffect, useState } from 'react';

import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { InputText } from 'primereact/inputtext';
import { Fieldset } from 'primereact/fieldset';
import { ConfirmDialogService, ToastService, useDataTable } from '@iamsoftware/react-hooks';

import { CustomerService as Service } from './CustomerService';
import { useCustomerDialog } from '../../../hooks/useCustomerDialog';
import { UploadExcel } from './UploadExcel';
import { CommonService } from '../../../service/CommonService';

const statuses = [
  { value: 'N', label: 'Đang hoạt động' },
  { value: 'Y', label: 'Ngừng hoạt động' }
];

export const Customers = () => {

  const dataKey = 'partyId';
  const header = 'Danh sách khách hàng';

  const [statusId, setStatusId] = useState(statuses[0].value);
  const [displayUploadExcel, setDisplayUploadExcel] = useState(false);

  const customerDialog = useCustomerDialog({
    service: Service as any,
    reloadLazyData: () => reloadLazyData()
  });

  useEffect(() => {
    if (statusId) {
      reloadLazyData();
    }
  }, [statusId]);

  const tableHeader = (
    <div className="flex align-items-center justify-content-between">
      <span>{header}</span>
      <div className="flex gap-1">
        <Button label="Thêm" icon="pi pi-plus" severity="success" className="mb-1 h-2rem" size="small" onClick={() => customerDialog.create()} />
        <Button label="Thêm từ Excel" icon="pi pi-file-excel" severity="help" className="mb-1 h-2rem" size="small" onClick={() => setDisplayUploadExcel(true)} />
      </div>
    </div>
  )

  const { render: renderDataTable, reloadLazyData } = useDataTable({
    tableHeader: tableHeader,
    dataKey,
    indexColumnWidth: 45,
    columns: [
      { field: 'pseudoId', header: 'Mã khách hàng', width: 150, matchMode: 'contains' },

      { field: 'partyTaxId', header: 'MST', width: 150, matchMode: 'contains' },
      { field: 'partyName', header: 'Tên khách hàng', minWidth: 250, matchMode: 'contains' },
      { field: 'address1', header: 'Địa chỉ', minWidth: 250, matchMode: 'contains' },
      { field: 'representativeName', header: 'Người đại diện', width: 150, matchMode: 'contains' },
      { field: 'emailAddress', header: 'Email', minWidth: 200, matchMode: 'contains' },
      { field: 'contactNumber', header: 'SĐT', width: 150, matchMode: 'contains' },
    ],
    actionColumnWidth: 120,
    getList(lazyLoadEvent: any): Promise<any> {
      return Service.getList(lazyLoadEvent, { disabled: statusId }).then()
    },
    initActions(items: Array<any>) {
      if (items) {
        items.forEach(item => {
          item.actions = [
            { icon: 'pi pi-eye', title: 'Xem chi tiết', className: 'p-button-info', command: () => customerDialog.view(item) },
            { icon: 'pi pi-pencil', title: 'Chỉnh sửa', className: 'p-button-warning', command: () => customerDialog.update(item) },
          ]
          if (item.disabled === 'Y') {
            item.actions.push({ icon: 'pi pi-unlock', title: 'Mở khóa', className: 'p-button-success', command: () => doEnable(item) },)
          } else {
            item.actions.push({ icon: 'pi pi-lock', title: 'Khóa', className: 'p-button-danger', command: () => doDisable(item) },)
          }
          item.actions.push({ icon: 'pi pi-trash', title: 'Xóa', className: 'p-button-secondary', command: () => doDelete(item) },)
        })
      }
    },
    originalProps: { scrollHeight: 'calc(100vh - 300px)' }
  })



  const doDisable = (item: any) => {
    if (item[dataKey]) {
      ConfirmDialogService.confirm('Khóa khách hàng', 'Bạn xác nhận muốn khóa khách hàng này?', () => {
        Service.disable(item[dataKey]).then((resp: any) => {
          if (resp && resp.success === 'N') {
            const msg = resp?.error || resp?.message || 'Khóa khách hàng thất bại';
            ToastService.error(msg);
          } else {
            ToastService.success();
            reloadLazyData();
          }
        }).catch((e: any) => {
          ToastService.error(e?.message || 'Khóa khách hàng thất bại');
        });
      })
    }
  }

  const doEnable = (item: any) => {
    if (item[dataKey]) {
      ConfirmDialogService.confirm('Mở khóa khách hàng', 'Bạn xác nhận mở khóa khách hàng này?', () => {
        Service.enable(item[dataKey]).then((resp: any) => {
          if (resp && resp.success === 'N') {
            const msg = resp?.error || resp?.message || 'Mở khóa khách hàng thất bại';
            ToastService.error(msg);
          } else {
            ToastService.success();
            reloadLazyData();
          }
        }).catch((e: any) => {
          ToastService.error(e?.message || 'Mở khóa khách hàng thất bại');
        });
      })
    }
  }

  const doDelete = (item: any) => {
    if (item[dataKey]) {
      ConfirmDialogService.confirm('Xóa khách hàng', 'Bạn xác nhận xóa khách hàng này?', () => {
        Service.delete(item[dataKey]).then((resp: any) => {
          if (resp && resp.success === 'N') {
            const msg = resp?.error || resp?.message || 'Xóa khách hàng thất bại';
            ToastService.error(msg);
          } else {
            ToastService.success();
            reloadLazyData();
          }
        }).catch((e: any) => {
          ToastService.error(e?.message || 'Xóa khách hàng thất bại');
        });
      })
    }
  }


  return (
    <div className="card border-none shadow-1 border-round-xl" style={{ backgroundColor: 'var(--surface-ground)' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .khach-hang-table .p-datatable-thead > tr:first-child > th {
          position: sticky !important; top: 0 !important; z-index: 10 !important;
          background: var(--surface-card) !important;
        }
        .khach-hang-table .p-datatable-thead > tr:nth-child(2) > th {
          position: sticky !important; top: 38px !important; z-index: 9 !important;
          background: var(--surface-card) !important;
        }
      `}} />
      <div className="col-12">
        <h3 className="m-0 mb-2" style={{ color: 'var(--text-color)', fontWeight: 'bold' }}>{header}</h3>
      </div>
      <div className="col-12 pb-0">
        <SelectButton value={statusId} options={statuses} onChange={e => setStatusId(e.value)} />
      </div>
      <div className="col-12 py-0 khach-hang-table">
        {renderDataTable()}
      </div>

      {customerDialog.render()}
      <UploadExcel display={displayUploadExcel} setDisplay={setDisplayUploadExcel} reloadLazyData={reloadLazyData} />
    </div>
  );
}